import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Zap, BarChart3 } from "lucide-react";

export const PRODUCTS = [
  {
    slug: "legacy-testing-suite",
    name: "Automated Legacy Testing Suite",
    tagline: "Verify 40-year-old business logic, automatically.",
    description:
      "A fully automated testing platform built for legacy code modernization. Extracts real behavior from mainframe, COBOL and Natural/Adabas systems, generates executable behavioral tests, and runs them in parallel against modernized components.",
    accent: "green",
    icon: Zap,
    image:
      "https://images.unsplash.com/photo-1680992046617-e2e35451bcdb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwzfHxkYXJrJTIwbW9kZXJuJTIwc2VydmVyJTIwcm9vbXxlbnwwfHx8fDE3Nzc1MjM2MzZ8MA&ixlib=rb-4.1.0&q=85",
    highlights: [
      "Automated behavior extraction from legacy source & runtime",
      "Parallel verification against modernized output",
      "Continuous regression detection with full auditability",
      "No vendor lock-in. Works alongside any modernization team.",
    ],
  },
  {
    slug: "ai-usage-analytics",
    name: "Enterprise AI Usage Analytics & Workflow Provenance",
    tagline: "Know exactly how AI is used across your organization.",
    description:
      "Enterprise-grade observability for generative AI. Tracks every model call, prompt, response and workflow artifact across your company, with provenance, cost, risk scoring and policy enforcement built in.",
    accent: "cyan",
    icon: BarChart3,
    image:
      "https://images.unsplash.com/photo-1638356554489-decb2e3a4c37?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxhcnRpZmljaWFsJTIwaW50ZWxsaWdlbmNlJTIwZ2xvd2luZyUyMG5vZGVzJTIwZGFya3xlbnwwfHx8fDE3Nzc1MjM2NDl8MA&ixlib=rb-4.1.0&q=85",
    highlights: [
      "Unified usage, spend & risk analytics across every LLM",
      "Per-team, per-workflow AI provenance trails",
      "Policy & compliance guardrails (SOC2, ISO, GDPR-ready)",
      "Deploy on your own cloud. Your data never leaves.",
    ],
  },
];

export default function ProductsTeaser() {
  return (
    <section
      data-testid="products-teaser"
      id="products"
      className="relative border-b border-white/10 bg-carbon"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-24 lg:py-32">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div>
            <div className="chip text-neon-cyan">&gt; /products</div>
            <h2 className="mt-3 font-display font-black text-4xl md:text-5xl lg:text-6xl text-white tracking-tighter leading-[0.95]">
              Two flagship products.{" "}
              <span className="text-glow-cyan text-neon-cyan">Built for scale.</span>
            </h2>
          </div>
          <p className="max-w-md text-white/60 text-sm leading-relaxed">
            Enterprise software we would deploy inside our own engineering org.
            Battle-tested. Auditable. Deployable on your own cloud.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {PRODUCTS.map((p, i) => (
            <motion.div
              key={p.slug}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className={`group relative overflow-hidden border border-white/10 bg-obsidian tracing-border ${
                p.accent === "green" ? "hover:border-neon-green/60" : "hover:border-neon-cyan/60"
              } transition-colors`}
              data-testid={`product-card-${p.slug}`}
            >
              <div
                className="absolute inset-0 opacity-[0.18] group-hover:opacity-30 transition-opacity"
                style={{
                  backgroundImage: `url(${p.image})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-br from-obsidian via-obsidian/90 to-transparent" />

              <div className="relative p-8 lg:p-10 min-h-[440px] flex flex-col">
                <div className="flex items-center gap-3">
                  <span
                    className={`h-10 w-10 inline-flex items-center justify-center border ${
                      p.accent === "green"
                        ? "border-neon-green/40 text-neon-green"
                        : "border-neon-cyan/40 text-neon-cyan"
                    }`}
                  >
                    <p.icon size={18} />
                  </span>
                  <span className="chip text-white/50">// product.0{i + 1}</span>
                </div>

                <h3 className="mt-6 font-display font-black text-3xl lg:text-4xl text-white leading-tight max-w-lg">
                  {p.name}
                </h3>

                <p
                  className={`mt-4 text-lg font-mono ${
                    p.accent === "green" ? "text-neon-green" : "text-neon-cyan"
                  }`}
                >
                  {p.tagline}
                </p>

                <p className="mt-4 text-sm text-white/70 leading-relaxed max-w-xl">
                  {p.description}
                </p>

                <ul className="mt-6 space-y-2">
                  {p.highlights.slice(0, 3).map((h) => (
                    <li key={h} className="flex items-start gap-3 text-sm text-white/70">
                      <span
                        className={`mt-1.5 h-1.5 w-1.5 ${
                          p.accent === "green" ? "bg-neon-green" : "bg-neon-cyan"
                        }`}
                      />
                      {h}
                    </li>
                  ))}
                </ul>

                <div className="mt-auto pt-8">
                  <Link
                    to={`/products/${p.slug}`}
                    data-testid={`product-learn-more-${p.slug}`}
                    className={`inline-flex items-center gap-2 text-sm font-mono uppercase tracking-widest ${
                      p.accent === "green" ? "text-neon-green" : "text-neon-cyan"
                    }`}
                  >
                    Learn more <ArrowUpRight size={16} />
                  </Link>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
